import { isNotDefined } from '@sgme/fp';
import { isEqual } from 'lodash-es';
import type { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import type { ProductCodesGridMetadata } from 'models/forms/fields/ProductCodesGridMetadata';

import { buildPatchedCellKey } from '../gridExtractor';

type ProductCodeData = {
  ProductCodeKey: string;
  ProductCodeValue: string;
  ProductCodeIsRequested: boolean;
  ProductCodeDisplayInTS: boolean;
};

type ProductCodeColumn = {
  id: keyof ProductCodeData;
  getValue: (row: ProductCodeData) => unknown;
};

const PRODUCT_CODE_COLUMNS: readonly ProductCodeColumn[] = [
  { id: 'ProductCodeValue', getValue: row => row.ProductCodeValue },
  {
    id: 'ProductCodeIsRequested',
    getValue: row => row.ProductCodeIsRequested,
  },
  {
    id: 'ProductCodeDisplayInTS',
    getValue: row => row.ProductCodeDisplayInTS,
  },
];

export function isProductCodesMetadata(meta: FieldMetadata): meta is ProductCodesGridMetadata {
  return meta.type === 'ProductCodesGrid';
}

function asProductCodeRows(value: unknown): readonly ProductCodeData[] {
  return Array.isArray(value) ? (value as readonly ProductCodeData[]) : [];
}

function getRowsByKey(rows: readonly ProductCodeData[]): ReadonlyMap<string, ProductCodeData> {
  return new Map(rows.map(row => [row.ProductCodeKey, row]));
}

export function productCodesGridCellExtractor(
  fieldId: string,
  meta: FieldMetadata,
  previous: unknown,
  current: unknown,
): readonly string[] {
  if (!isProductCodesMetadata(meta)) {
    return [];
  }

  const previousRows = getRowsByKey(asProductCodeRows(previous));
  const currentRows = getRowsByKey(asProductCodeRows(current));

  const visibleColumns = meta.isDisplayTermsheetColumnVisible
    ? PRODUCT_CODE_COLUMNS
    : PRODUCT_CODE_COLUMNS.filter(({ id }) => id !== 'ProductCodeDisplayInTS');

  return meta.rows.flatMap((rowMetadata, rowIndex) => {
    const currentRow = currentRows.get(rowMetadata.key);

    if (isNotDefined(currentRow)) {
      return [];
    }

    const previousRow = previousRows.get(rowMetadata.key);

    if (isNotDefined(previousRow)) {
      return visibleColumns.map(column => buildPatchedCellKey(fieldId, rowIndex, column.id));
    }

    return visibleColumns.flatMap(column =>
      isEqual(column.getValue(previousRow), column.getValue(currentRow))
        ? []
        : [buildPatchedCellKey(fieldId, rowIndex, column.id)],
    );
  });
}
